<template>
  <form class="req-form" @submit.prevent="handleSubmit">
    <div class="req-form__mode">
      <button
        type="button"
        class="req-form__mode-btn"
        :class="{ 'req-form__mode-btn--active': mode === 'specific' }"
        @click="mode = 'specific'"
      >
        <BaseIcon name="cubes" size="14" />
        {{ $t('resources.requirement.mode_specific') }}
      </button>
      <button
        type="button"
        class="req-form__mode-btn"
        :class="{ 'req-form__mode-btn--active': mode === 'category' }"
        @click="mode = 'category'"
      >
        <BaseIcon name="list-ul" size="14" />
        {{ $t('resources.requirement.mode_category') }}
      </button>
    </div>

    <SelectInput
      v-if="mode === 'specific'"
      id="req_resource"
      :label="$t('resources.requirement.choose_resource')"
      v-model="form.resource_id"
      :options="resourceOptions"
      :error="errors.target"
    />
    <div v-else class="form-group">
      <label for="req_category">{{ $t('resources.requirement.choose_category') }}</label>
      <input
        id="req_category"
        list="req_category_options"
        v-model="form.category"
        :placeholder="$t('resources.fields.category_placeholder')"
      />
      <datalist id="req_category_options">
        <option v-for="cat in categories" :key="cat" :value="cat" />
      </datalist>
      <span v-if="errors.target" class="form-group__error">{{ errors.target }}</span>
    </div>

    <div class="req-form__row req-form__row--3">
      <NumberInput
        id="req_quantity"
        :label="$t('resources.requirement.quantity')"
        v-model="form.quantity"
        :min="1"
      />
      <NumberInput
        id="req_days_before"
        :label="$t('resources.requirement.days_before')"
        v-model="form.days_before"
        :min="0"
      />
      <NumberInput
        id="req_days_after"
        :label="$t('resources.requirement.days_after')"
        v-model="form.days_after"
        :min="0"
      />
    </div>
    <p class="req-form__hint">{{ $t('resources.requirement.days_hint') }}</p>

    <SelectInput
      id="req_fulfillment"
      :label="$t('resources.requirement.fulfillment')"
      v-model="form.fulfillment"
      :options="fulfillmentOptions"
    />

    <TextInput
      id="req_note"
      :label="$t('resources.requirement.note')"
      v-model="form.note"
      placeholder="Bijv. 2 vlonders extra"
    />

    <!-- Availability -->
    <div v-if="!appointmentDate" class="req-form__availability req-form__availability--muted">
      <BaseIcon name="info" size="14" />
      {{ $t('resources.requirement.no_date') }}
    </div>
    <div
      v-else-if="form.fulfillment === 'own' && hasTarget"
      class="req-form__availability"
      :class="{
        'req-form__availability--ok': availability && availability.available,
        'req-form__availability--warn': availability && !availability.available,
      }"
    >
      <template v-if="checking">
        <LoadingSpinner size="sm" />
        {{ $t('resources.requirement.availability_checking') }}
      </template>
      <template v-else-if="availability">
        <BaseIcon
          :name="availability.available ? 'check-circle' : 'exclamation-triangle'"
          size="14"
        />
        <span>
          <template v-if="availability.available">
            {{ $t('resources.requirement.availability_ok') }}
          </template>
          <template v-else-if="availability.resource_status">
            {{ $t(`resources.statuses.${availability.resource_status}`) }}
          </template>
          <template v-else>
            {{
              $t('resources.requirement.availability_conflict', {
                days: availability.conflicts.length,
              })
            }}
          </template>
          <span class="req-form__period">· {{ formatDateRange(period.from, period.to) }}</span>
        </span>
      </template>
    </div>

    <div class="req-form__actions">
      <Btn type="button" variant="outlined" @click="onCancel && onCancel()">
        {{ $t('common.cancel') }}
      </Btn>
      <Btn type="submit" variant="primary" :loading="saving">
        {{ $t('resources.actions.add') }}
      </Btn>
    </div>
  </form>
</template>

<script>
  import { defineComponent, ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
  import { resourceService } from '@/services/resourceService';
  import { formatDateRange } from '@/constants/resourceConstants';
  import Btn from '@/components/partials/Btn.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const addDays = (dateKey, n) => {
    const d = new Date(`${dateKey}T00:00:00`);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };

  export default defineComponent({
    name: 'AddResourceRequirementModal',
    components: { Btn, LoadingSpinner, TextInput, SelectInput, NumberInput, BaseIcon },
    props: {
      workOrder: { type: Object, required: true },
      onSubmit: { type: Function, required: true },
      onCancel: { type: Function, default: null },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const mode = ref('specific');
      const saving = ref(false);
      const checking = ref(false);
      const availability = ref(null);
      const errors = ref({});
      const resources = ref([]);
      const categories = ref([]);

      const form = ref({
        resource_id: '',
        category: '',
        quantity: 1,
        days_before: 0,
        days_after: 0,
        fulfillment: 'own',
        note: '',
      });

      const appointmentDate = computed(() =>
        props.workOrder?.appointment_start
          ? new Date(props.workOrder.appointment_start).toISOString().slice(0, 10)
          : null
      );
      const period = computed(() => {
        if (!appointmentDate.value) return { from: null, to: null };
        return {
          from: addDays(appointmentDate.value, -(Number(form.value.days_before) || 0)),
          to: addDays(appointmentDate.value, Number(form.value.days_after) || 0),
        };
      });

      const hasTarget = computed(() =>
        mode.value === 'specific' ? !!form.value.resource_id : !!form.value.category?.trim()
      );

      const resourceOptions = computed(() => {
        const vehicles = resources.value.filter((r) => r.kind === 'vehicle');
        const vehicleName = (id) => vehicles.find((v) => v.id === id)?.name;
        return [
          { value: '', label: '—' },
          ...resources.value
            .filter((r) => r.status !== 'retired')
            .map((r) => {
              const loc = r.parent_resource_id ? vehicleName(r.parent_resource_id) : null;
              const parts = [r.name];
              if (r.code) parts.push(`(${r.code})`);
              if (loc) parts.push(`· ${loc}`);
              if (r.status !== 'available') parts.push(`· ${$t(`resources.statuses.${r.status}`)}`);
              return { value: r.id, label: parts.join(' ') };
            }),
        ];
      });

      const fulfillmentOptions = [
        { value: 'own', label: $t('resources.fulfillment.own') },
        { value: 'rent', label: $t('resources.fulfillment.rent') },
      ];

      onMounted(async () => {
        const [res, cats] = await Promise.allSettled([
          resourceService.getAll(),
          resourceService.getCategories(),
        ]);
        resources.value = res.status === 'fulfilled' ? res.value : [];
        categories.value = cats.status === 'fulfilled' ? cats.value : [];
      });

      // When picking a specific resource, remember its category for the payload.
      watch(
        () => form.value.resource_id,
        (id) => {
          const r = resources.value.find((x) => x.id === id);
          if (r && r.kind !== 'vehicle' && r.category) form.value.category = r.category;
        }
      );

      let checkTimer = null;
      const checkAvailability = async () => {
        availability.value = null;
        if (!appointmentDate.value || !hasTarget.value || form.value.fulfillment !== 'own') return;
        checking.value = true;
        try {
          availability.value = await resourceService.checkAvailability({
            resource_id: mode.value === 'specific' ? form.value.resource_id : null,
            category: mode.value === 'category' ? form.value.category.trim() : null,
            quantity: Number(form.value.quantity) || 1,
            from: period.value.from,
            to: period.value.to,
            exclude_work_order_id: props.workOrder.id,
          });
        } catch {
          availability.value = null;
        } finally {
          checking.value = false;
        }
      };

      watch(
        () => [
          mode.value,
          form.value.resource_id,
          form.value.category,
          form.value.quantity,
          form.value.days_before,
          form.value.days_after,
          form.value.fulfillment,
        ],
        () => {
          clearTimeout(checkTimer);
          checkTimer = setTimeout(checkAvailability, 350);
        }
      );

      const handleSubmit = async () => {
        errors.value = {};
        if (!hasTarget.value) {
          errors.value.target = $t('resources.validation.resource_or_category');
          return;
        }
        saving.value = true;
        try {
          await props.onSubmit({
            resource_id: mode.value === 'specific' ? form.value.resource_id : null,
            category:
              mode.value === 'category' ? form.value.category.trim() : form.value.category || null,
            quantity: Number(form.value.quantity) || 1,
            days_before: Number(form.value.days_before) || 0,
            days_after: Number(form.value.days_after) || 0,
            fulfillment: form.value.fulfillment,
            note: form.value.note?.trim() || null,
            source: 'manual',
          });
        } finally {
          saving.value = false;
        }
      };

      return {
        mode,
        form,
        errors,
        saving,
        checking,
        availability,
        categories,
        resourceOptions,
        fulfillmentOptions,
        appointmentDate,
        period,
        hasTarget,
        handleSubmit,
        formatDateRange,
      };
    },
  });
</script>

<style scoped lang="scss">
  .req-form {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;

    &__mode {
      display: inline-flex;
      padding: 3px;
      border-radius: 8px;
      background: var(--slate-100);
      gap: 2px;
    }

    &__mode-btn {
      flex: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      padding: 0.375rem 0.75rem;
      border: none;
      border-radius: 6px;
      background: transparent;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-600);
      cursor: pointer;

      &--active {
        background: white;
        color: var(--slate-900);
        @include shadow('xs');
      }
    }

    &__row {
      display: grid;
      gap: 0.75rem;
      grid-template-columns: 1fr 1fr;

      &--3 {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__hint {
      margin: -0.5rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__availability {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border-radius: 8px;
      font-size: 0.8125rem;
      border: 1px solid var(--slate-200);
      background: var(--slate-50);
      color: var(--slate-600);
      min-height: 38px;

      &--ok {
        border-color: var(--green-200);
        background: var(--green-50);
        color: var(--green-700);
      }
      &--warn {
        border-color: var(--amber-200);
        background: var(--amber-50);
        color: var(--amber-800);
      }
      &--muted {
        color: var(--slate-500);
      }
    }

    &__period {
      color: inherit;
      opacity: 0.8;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
